import { Injectable, signal, computed, inject } from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';
import { filter } from 'rxjs';
import { doc, onSnapshot, updateDoc } from 'firebase/firestore';
import { db } from './firebase.init';
import { BoxType, SystemConfig } from '../models/types';

@Injectable({
  providedIn: 'root'
})
export class ConfigService {
  private router = inject(Router);

  private configSignal = signal<SystemConfig | null>(null);
  private loadingSignal = signal<boolean>(true);
  private errorSignal = signal<string | null>(null);

  // Reactive wall-clock time signal to force time-based computed signals to update
  private currentTimeSignal = signal<number>(Date.now());

  private unsubscribeListener?: () => void;

  // Read-only public signals
  readonly config = computed(() => this.configSignal());
  readonly loading = computed(() => this.loadingSignal());
  readonly error = computed(() => this.errorSignal());

  // Check if today is Sunday in Bulgaria
  readonly isSunday = computed(() => {
    // Reading currentTimeSignal registers time as a reactive signal dependency
    this.currentTimeSignal();

    try {
      const sofiaTimeStr = new Date().toLocaleString('en-US', { timeZone: 'Europe/Sofia' });
      const sofiaDate = new Date(sofiaTimeStr);
      return sofiaDate.getDay() === 0; // 0 is Sunday
    } catch (e) {
      console.warn('Timezone calculation error for Sunday check: ', e);
      return new Date().getDay() === 0;
    }
  });

  // Check if current time is within allowed working hours in Bulgaria (Europe/Sofia timezone)
  readonly isWithinWorkingHours = computed(() => {
    // Reading currentTimeSignal registers time as a reactive signal dependency
    this.currentTimeSignal();

    if (this.isSunday()) {
      return false; // Sunday is a day off
    }

    const cfg = this.configSignal();
    if (!cfg) return false;

    try {
      const sofiaTimeStr = new Date().toLocaleString('en-US', { timeZone: 'Europe/Sofia' });
      const sofiaDate = new Date(sofiaTimeStr);
      const currentLocalHour = sofiaDate.getHours();

      const startHour = cfg.order_start_hour;
      const endHour = cfg.order_end_hour;

      return currentLocalHour >= startHour && currentLocalHour < endHour;
    } catch (e) {
      console.warn('Timezone calculation error: ', e);
      const fallbackHour = new Date().getHours();
      const startHour = cfg.order_start_hour;
      const endHour = cfg.order_end_hour;
      return fallbackHour >= startHour && fallbackHour < endHour;
    }
  });

  // Main status override to see if user can place order now
  readonly isOrderingCurrentlyPossible = computed(() => {
    const cfg = this.configSignal();
    if (!cfg) return false;
    return cfg.ordering_enabled && this.isWithinWorkingHours();
  });

  constructor() {
    this.initRealtimeConfig();
    this.initReactiveTimeTracking();
  }

  retryLoadConfig() {
    this.loadingSignal.set(true);
    this.errorSignal.set(null);
    this.initRealtimeConfig();
  }

  // Force an immediate refresh of wall-clock time
  refreshTime() {
    this.currentTimeSignal.set(Date.now());
  }

  private initReactiveTimeTracking() {
    // 1. Refresh time on every route navigation end
    this.router.events.pipe(
      filter(event => event instanceof NavigationEnd)
    ).subscribe(() => {
      this.refreshTime();
    });

    // 2. Periodic background refresh every 15 seconds to handle staying on a page as 3:00 PM arrives
    if (typeof window !== 'undefined') {
      setInterval(() => {
        this.refreshTime();
      }, 15000);
    }
  }

  private initRealtimeConfig() {
    if (this.unsubscribeListener) {
      this.unsubscribeListener();
      this.unsubscribeListener = undefined;
    }

    const configDocRef = doc(db, 'system_config', 'status');

    this.unsubscribeListener = onSnapshot(configDocRef, (snapshot) => {
      if (snapshot.exists()) {
        this.configSignal.set(snapshot.data() as SystemConfig);
        this.errorSignal.set(null);
        this.loadingSignal.set(false);

        // If connection is restored while on system-error page, return to home page
        if (this.router.url === '/system-error') {
          this.router.navigate(['/']);
        }
      } else {
        const errMsg = 'Неуспешно зареждане на системните настройки.';
        console.error(errMsg);
        this.configSignal.set(null);
        this.errorSignal.set(errMsg);
        this.loadingSignal.set(false);

        if (this.router.url !== '/system-error') {
          this.router.navigate(['/system-error']);
        }
      }
    }, (error) => {
      const errMsg = 'Грешка при връзката с базата данни. Моля, проверете вашата интернет връзка.';
      console.error(errMsg, error);
      this.configSignal.set(null);
      this.errorSignal.set(errMsg);
      this.loadingSignal.set(false);

      if (this.router.url !== '/system-error') {
        this.router.navigate(['/system-error']);
      }
    });
  }

  // Update global settings
  async updateConfig(partialConfig: Partial<SystemConfig>, adminUid: string) {
    const configDocRef = doc(db, 'system_config', 'status');
    const updateData = {
      ...partialConfig,
      updated_at: new Date(),
      updated_by: adminUid
    };
    return updateDoc(configDocRef, updateData);
  }

  // Helper to fetch delivery fee based on selected location id
  getDeliveryFee(locationId: string): number {
    const activeConfig = this.configSignal();
    if (!activeConfig || !activeConfig.delivery_fees) {
      return 0;
    }
    const key = locationId as keyof typeof activeConfig.delivery_fees;
    return activeConfig.delivery_fees[key] ?? 0;
  }

  // Helper to resolve minimum order price based on selected location id
  getMinimumOrderPrice(locationId?: string): number {
    const activeConfig = this.configSignal();
    if (!activeConfig) {
      return 0;
    }

    if (!locationId) {
      return activeConfig.minimum_order_price ?? 0;
    }

    // Group 2 locations (villages outside Lom with higher delivery fee and higher minimum order price)
    const group2 = ['kovachitsa', 'staliyska_mahala', 'traykovo', 'rasovo'];
    if (group2.includes(locationId)) {
      return activeConfig.minimum_order_price_outside ?? activeConfig.minimum_order_price;
    }

    return activeConfig.minimum_order_price;
  }

  // Helper to get box price based on box type
  getBoxPrice(boxType: BoxType): number {
    const activeConfig = this.configSignal();
    if (!activeConfig || !activeConfig.box_prices) {
      return 0;
    }
    const price = activeConfig.box_prices[boxType];
    return price ?? 0;
  }
}
